import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const FILTER_GROUPS = [
  { key: "audience", label: "Audience", options: [["men", "Men"], ["boys", "Boys"]] },
  { key: "category", label: "Category", options: [["kurta", "Kurta"], ["kurta-set", "Kurta Set"]] },
];

function FilterPanel({ params, setParam, filters }) {
  const dynamic = [
    { key: "occasion", label: "Occasion", options: filters.occasions?.map((o) => [o, o]) || [] },
    { key: "fabric", label: "Fabric", options: filters.fabrics?.map((o) => [o, o]) || [] },
    { key: "color", label: "Colour", options: filters.colors?.map((o) => [o, o]) || [] },
    { key: "size", label: "Size", options: filters.sizes?.map((o) => [o, o]) || [] },
  ];
  const groups = [...FILTER_GROUPS, ...dynamic];
  return (
    <div className="space-y-8">
      {groups.map((g) => (
        <div key={g.key}>
          <div className="uplabel mb-4">{g.label}</div>
          <div className="flex flex-wrap gap-2">
            {g.options.map(([val, label]) => {
              const active = params.get(g.key) === String(val);
              return (
                <button key={val} data-testid={`filter-${g.key}-${val}`}
                  onClick={() => setParam(g.key, active ? null : val)}
                  className={`px-4 py-2 text-sm border transition-colors ${active ? "bg-[var(--olive)] text-[#FBF7DE] border-[var(--olive)]" : "border-[var(--line)] text-[var(--bronze)] hover:border-[var(--olive)]"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === null || value === undefined) next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const query = params.toString();
  useEffect(() => {
    setLoading(true);
    api.get(`/products?${query}`).then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
    document.title = "Shop — KKashigari India";
  }, [query]);

  useEffect(() => {
    api.get("/products/filters").then(({ data }) => setFilters(data));
  }, []);

  const sort = params.get("sort") || "featured";
  const activeCount = useMemo(() => {
    let c = 0;
    ["audience", "category", "occasion", "fabric", "color", "size"].forEach((k) => params.get(k) && c++);
    return c;
  }, [params]);

  const clearAll = () => {
    const next = new URLSearchParams();
    const s = params.get("search");
    if (s) next.set("search", s);
    setParams(next);
  };

  const searchTerm = params.get("search");

  return (
    <div data-testid="shop-page" className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 lg:py-16">
      <div className="mb-10">
        <div className="uplabel mb-3">The Collection</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[var(--bronze)]">
          {searchTerm ? `Results for “${searchTerm}”` : "All Kurtas & Sets"}
        </h1>
      </div>

      <div className="flex gap-12">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="flex items-center justify-between mb-6">
            <span className="uplabel">Filters</span>
            {activeCount > 0 && <button onClick={clearAll} data-testid="clear-filters" className="text-xs text-[var(--olive)] hover:text-[var(--bronze)] transition-colors">Clear all</button>}
          </div>
          <FilterPanel params={params} setParam={setParam} filters={filters} />
        </aside>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-8 pb-4 border-b border-[var(--line)]">
            <div className="flex items-center gap-3">
              <Sheet>
                <SheetTrigger asChild>
                  <button data-testid="mobile-filter-btn" className="lg:hidden flex items-center gap-2 uplabel text-[var(--bronze)]">
                    <SlidersHorizontal className="w-4 h-4" /> Filters {activeCount > 0 && `(${activeCount})`}
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-[#FBF7DE] border-[var(--line)] overflow-y-auto">
                  <SheetHeader><SheetTitle className="font-serif text-2xl text-[var(--bronze)]">Filters</SheetTitle></SheetHeader>
                  <div className="mt-8"><FilterPanel params={params} setParam={setParam} filters={filters} /></div>
                  {activeCount > 0 && <button onClick={clearAll} className="uplabel text-[var(--olive)] mt-8">Clear all</button>}
                </SheetContent>
              </Sheet>
              <span className="text-sm text-[var(--olive)] hidden sm:block">{products.length} pieces</span>
            </div>

            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger data-testid="sort-select" className="w-48 border-[var(--line)] bg-transparent text-[var(--bronze)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#FBF7DE] border-[var(--line)]">
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {activeCount > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {["audience", "category", "occasion", "fabric", "color", "size"].map((k) => {
                const v = params.get(k);
                if (!v) return null;
                return (
                  <button key={k} onClick={() => setParam(k, null)} className="flex items-center gap-1.5 text-xs bg-[#F3EECB] px-3 py-1.5 text-[var(--bronze)]">
                    {v} <X className="w-3 h-3" />
                  </button>
                );
              })}
            </div>
          )}

          {loading ? (
            <div className="py-32 text-center uplabel text-[var(--olive)]">Loading…</div>
          ) : products.length === 0 ? (
            <div className="py-32 text-center">
              <p className="font-serif text-2xl text-[var(--bronze)]">No pieces match your filters</p>
              <button onClick={clearAll} className="uplabel text-[var(--olive)] mt-4 link-underline">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
